import { useCallback, useEffect, useRef, useState } from 'react';
import { chatService } from '../services/chatApi';

/**
 * Position and size of the Conversations window.
 *
 * Three layers, in order of authority:
 *
 *   1. the account   — chat_ui_prefs on the server, so the window comes back in
 *                      the same place on a new browser or a second device
 *   2. localStorage  — a per-browser cache, read synchronously so the window
 *                      does not paint in the default spot and then jump when the
 *                      server answers
 *   3. the default   — top-right, the same 420px width the slide-out panel had
 *
 * Everything is clamped to the current viewport on read. A window saved on a
 * 32" monitor must not open off-screen on a laptop, and the member has no way to
 * recover it if it does.
 */

const CACHE_PREFIX = 'cgs.chat.layout';

export const MIN_WIDTH = 320;
export const MIN_HEIGHT = 360;

// Matches the old panel's width. Height is a window rather than full-bleed now,
// so it takes a generous default and gets capped to the viewport below.
const DEFAULT_WIDTH = 420;
const DEFAULT_HEIGHT = 640;

const MARGIN = 16;
// Roughly the site header, so the default position reads as dropping out of the
// Conversations icon rather than covering the nav.
const TOP_OFFSET = 88;
// However far it is dragged, this much of the window stays grabbable.
const KEEP_VISIBLE = 120;

function viewport() {
  return {
    width: window.innerWidth || DEFAULT_WIDTH,
    height: window.innerHeight || DEFAULT_HEIGHT,
  };
}

export function defaultLayout() {
  const { width: vw, height: vh } = viewport();
  const width = Math.min(DEFAULT_WIDTH, Math.max(MIN_WIDTH, vw - MARGIN * 2));
  const height = Math.min(DEFAULT_HEIGHT, Math.max(MIN_HEIGHT, vh - TOP_OFFSET - MARGIN));

  return {
    x: Math.max(MARGIN, vw - width - MARGIN),
    y: TOP_OFFSET,
    width,
    height,
  };
}

/** Keep a layout inside the current viewport, and grabbable. */
export function clampLayout(layout) {
  const { width: vw, height: vh } = viewport();

  const width = Math.round(Math.min(Math.max(layout.width, MIN_WIDTH), Math.max(MIN_WIDTH, vw - MARGIN * 2)));
  const height = Math.round(Math.min(Math.max(layout.height, MIN_HEIGHT), Math.max(MIN_HEIGHT, vh - MARGIN * 2)));

  return {
    width,
    height,
    x: Math.round(Math.min(Math.max(layout.x, KEEP_VISIBLE - width), vw - KEEP_VISIBLE)),
    // Never above the top edge: the header is the only drag handle, and a window
    // dragged past the top could not be pulled back down.
    y: Math.round(Math.min(Math.max(layout.y, 0), Math.max(0, vh - KEEP_VISIBLE))),
  };
}

function isLayout(value) {
  return Boolean(value) && ['x', 'y', 'width', 'height'].every((k) => Number.isFinite(Number(value[k])));
}

function readCache(memberId) {
  if (!memberId) return null;
  try {
    const raw = localStorage.getItem(`${CACHE_PREFIX}.${memberId}`);
    const parsed = raw ? JSON.parse(raw) : null;
    return isLayout(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeCache(memberId, layout) {
  if (!memberId) return;
  try {
    localStorage.setItem(`${CACHE_PREFIX}.${memberId}`, JSON.stringify(layout));
  } catch {
    // Private mode or quota. Position is an enhancement, not a requirement.
  }
}

/**
 * @param {number|null} memberId  who to load and save for
 * @param {boolean} active        only fetch once the window has actually been
 *                                opened — the panel component is mounted on
 *                                every page, and a member who never opens chat
 *                                should not cost a request
 */
export default function useChatPanelLayout(memberId, active) {
  const [layout, setLayoutState] = useState(() => clampLayout(readCache(memberId) || defaultLayout()));
  const saveTimer = useRef(null);
  const lastSaved = useRef(null);
  const loadedFor = useRef(null);

  // A cached layout for a different member (shared computer) must not leak in.
  useEffect(() => {
    setLayoutState(clampLayout(readCache(memberId) || defaultLayout()));
    lastSaved.current = null;
    loadedFor.current = null;
  }, [memberId]);

  useEffect(() => {
    if (!active || !memberId || loadedFor.current === memberId) return undefined;
    loadedFor.current = memberId;

    let cancelled = false;
    chatService
      .getUiPrefs()
      .then((response) => {
        const stored = response?.data?.panelLayout;
        if (cancelled || !isLayout(stored)) return;
        const next = clampLayout(stored);
        lastSaved.current = JSON.stringify(stored);
        setLayoutState(next);
        writeCache(memberId, next);
      })
      // A missing or unreachable preference is not worth surfacing — the window
      // simply opens where it always has.
      .catch(() => {});

    return () => { cancelled = true; };
  }, [active, memberId]);

  const persist = useCallback((next) => {
    if (!memberId) return;

    const serialized = JSON.stringify(next);
    if (serialized === lastSaved.current) return;

    // Debounced, and the cache write is inside the debounce as well: a drag
    // emits a move per frame, and neither a request nor a synchronous
    // localStorage write belongs on that path.
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      lastSaved.current = serialized;
      writeCache(memberId, next);
      chatService.updatePanelLayout(next).catch(() => {
        // Let the next change retry rather than reporting a failure the member
        // cannot act on.
        lastSaved.current = null;
      });
    }, 600);
  }, [memberId]);

  /** Live update during a drag or resize; persistence is debounced inside. */
  const setLayout = useCallback((updater) => {
    setLayoutState((current) => {
      const next = clampLayout(typeof updater === 'function' ? updater(current) : updater);
      persist(next);
      return next;
    });
  }, [persist]);

  const resetLayout = useCallback(() => setLayout(defaultLayout()), [setLayout]);

  // Re-clamp when the viewport changes, so a window parked against the right
  // edge is still reachable after the browser is made narrower.
  useEffect(() => {
    const onResize = () => setLayoutState((current) => clampLayout(current));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  return { layout, setLayout, resetLayout };
}
